import React, { useContext, useEffect, useMemo, useState } from "react"
import { useInfiniteQuery, useQueryClient } from "react-query"
import MergeInvoiceModal from "src/main/Billing/Payments/MergeInvoiceModal"
import PaymentModal from "src/main/Billing/Payments/PaymentModal"
import PaymentsTable from "src/main/Billing/Payments/PaymentsTable"
import SettlePaymentModal from "src/main/Billing/Payments/SettlePaymentModal"
import { useMergeInvoice } from "src/main/Billing/Payments/hooks"

import Heading from "src/components/Heading"
import OverflowMenu from "src/components/OverflowMenu"
import Tooltip from "src/components/Tooltip"

import { queryInvoiceTxns } from "src/api/Billing/Payments"

import { getCurrentMarinaSlug } from "src/utils/url/parsing/marina"

import PrintReceiptModal from "../../PrintReceiptModal"
import { SaleDetailsContext } from "../SaleDetailsContext"

const SalePayments = () => {
  const marinaSlug = getCurrentMarinaSlug()
  const {
    sale,
    payments: { data: payments, isLoading, isError },
    refreshItems,
    refreshPayments,
    paymentMethods,
    receiptPrinters,
    marinaAccess,
    editInvoiceDueDates,
  } = useContext(SaleDetailsContext)
  const { status: saleStatus } = sale

  const [isEditingPayment, setIsEditingPayment] = useState(false)
  const [isSettlingPayment, setIsSettlingPayment] = useState(false)
  const [editablePayment, setEditablePayment] = useState(null)
  const [paymentToSettle, setPaymentToSettle] = useState(null)
  const [selectedPrintInvoiceId, setPrintingInvoiceId] = useState(null)

  const queryClient = useQueryClient()
  const paymentId = editablePayment?.id

  const {
    isFetching: isLoadingInvoiceTxns,
    isError: isErrorInvoiceTxns,
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["invoice_txns", paymentId, marinaSlug],
    queryFn: ({ pageParam = 1 }) =>
      queryInvoiceTxns({ invoiceId: paymentId, marinaSlug, page: pageParam }),
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.length) {
        return pages.length + 1
      }
    },
    enabled: Boolean(paymentId),
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage()
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage])

  const invoiceTxns = useMemo(
    () => (data?.pages || []).flatMap((page) => page),
    [data]
  )
  const resetInvoiceTxns = () => {
    queryClient.resetQueries({
      queryKey: ["invoice_txns", paymentId, marinaSlug],
    })
  }

  // This function is curried by the payment rows and used when a user clicks
  // "Open" for a payment
  const onEditPayment = (payment) => () => {
    setIsEditingPayment(true)
    setEditablePayment(payment)
  }

  const onEditPaymentClose = () => {
    setIsEditingPayment(false)
  }

  const afterEditPaymentLeave = () => {
    resetInvoiceTxns()
    setEditablePayment(null)
  }

  const onSettlePaymentClick = (payment) => {
    setPaymentToSettle(payment)
    setIsSettlingPayment(true)
  }

  const onSettleClose = () => {
    setIsSettlingPayment(false)
  }

  const onSettlePaymentModalSubmit = () => {
    onSettleClose()
    resetInvoiceTxns()
    refreshItems()
    refreshPayments()
  }

  const {
    invoiceToMerge,
    isMergingInvoice,
    onMergeInvoiceClick,
    onMergeInvoiceClose,
    onMergeSubmitSuccess,
  } = useMergeInvoice({ resetInvoiceTxns, refreshItems, refreshPayments })

  const renderMenuItems = (payment) => {
    const isDisabled =
      saleStatus === "processing" || receiptPrinters.length === 0
    const isNotPermitted = !marinaAccess.manageHardware
    const notPermittedMessage =
      "The ability to print receipts is available within the POS module.\nContact your Dockwa representative or our support team to learn more."
    const disabledMessage =
      saleStatus === "processing"
        ? "Unable to print receipt when payment is processing"
        : "Configure a receipt printer in Dockwa settings (Account → Settings → Hardware)"

    const menuItem = (
      <OverflowMenu.Item
        label="Print receipt"
        onClick={() => setPrintingInvoiceId(payment.id)}
        disabled={isNotPermitted || isDisabled}
      />
    )

    if (isNotPermitted) {
      return (
        <Tooltip placement="top" variant="dark" text={notPermittedMessage}>
          {menuItem}
        </Tooltip>
      )
    }

    if (isDisabled) {
      return (
        <Tooltip placement="top" variant="dark" text={disabledMessage}>
          {menuItem}
        </Tooltip>
      )
    }

    return menuItem
  }

  return (
    <div className="pb-5">
      <div className="mb-4">
        <Heading.SubSectionHeader>Payments</Heading.SubSectionHeader>
      </div>
      <div className="overflow-x-auto md:overflow-visible">
        <PaymentsTable
          payments={payments}
          editPayment={onEditPayment}
          mergeInvoice={onMergeInvoiceClick}
          settlePayment={onSettlePaymentClick}
          isLoading={isLoading}
          isError={isError}
          renderMenuItems={renderMenuItems}
        />
      </div>

      <PaymentModal
        payment={editablePayment}
        invoiceTxns={invoiceTxns}
        setEditablePayment={setEditablePayment}
        refreshPayments={refreshPayments}
        refreshItems={refreshItems}
        paymentMethods={paymentMethods}
        isOpen={isEditingPayment}
        onClose={onEditPaymentClose}
        isLoadingInvoiceTxns={isLoadingInvoiceTxns || hasNextPage !== false}
        isErrorInvoiceTxns={isErrorInvoiceTxns}
        afterLeave={afterEditPaymentLeave}
        saleId={sale.id}
        editInvoiceDueDates={editInvoiceDueDates}
      />
      {/*
        See PaymentModal.jsx for the comment that explains why we
        render the SettlePaymentModal both here and in the PaymentModal
        component
      */}
      <SettlePaymentModal
        payment={paymentToSettle}
        paymentMethods={paymentMethods}
        onPaymentSettle={onSettlePaymentModalSubmit}
        isOpen={isSettlingPayment}
        onClose={onSettleClose}
        afterLeave={() => setPaymentToSettle(null)}
      />
      {selectedPrintInvoiceId ? (
        <PrintReceiptModal
          onClose={() => setPrintingInvoiceId(null)}
          invoiceId={selectedPrintInvoiceId}
        />
      ) : null}
      <MergeInvoiceModal
        invoice={invoiceToMerge}
        availableInvoices={payments}
        isOpen={isMergingInvoice}
        onClose={onMergeInvoiceClose}
        onMergeSubmitSuccess={onMergeSubmitSuccess}
      />
    </div>
  )
}

export default SalePayments
